/**
 * A proposed funnel on an empty canvas — drafting, then the review. Story 27.4.
 *
 * Visual Reference: `design/screens/editor-build.html`, states `drafting` and `proposal`. The
 * header above this is `EditorChrome` in its `pending` mode: nothing exists yet, and the header
 * says so.
 *
 * DRAFTING IS NAMED STAGES, NEVER A SPINNER. The route answers in one response (12 to 32 s on the
 * seeded model, Story 27.3's record), so the four stages advance on the clock and all complete
 * when it answers — and the call gives up at `DRAFT_TIMEOUT_MS`, past the server's own 60 s, so a
 * wait that does not end cannot happen (the AC's "never a spinner that does not end"). A draft
 * that fails, is refused for the day's limit or the kill switch, or finds drafting down says why
 * and offers the templates (`/library`) instead.
 *
 * THE REVIEW CHANGES NOTHING UNTIL ACCEPT. Ticks are local; "Draft it again as" asks the route
 * for that kind (it carries it); Discard forgets the request and leaves — no funnel row
 * (DECISION-167). Accept creates the funnel with the proposal's archetype, loads it, and applies
 * the parts through `edit()` (`proposal.ts`), so each undoes as its own step; the editor then
 * opens on it in place, with those steps on the stack.
 *
 * Deviations from the drawing, each listed in the story's AC review: the column and the card sit
 * at `--mdx-measure` where the export draws 760px and 640px (DECISION-064's precedent for a
 * one-off width); the name is "Untitled funnel", not the drawn "Site plan enquiry" (Q-138); and
 * the ideal customer's line beneath the bands is composed, since the drawing predates
 * DECISION-180.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ARCHETYPES, type Archetype, type Block, type Funnel } from '@leadgible/schema'
import { ApiFailure, apiGet, apiPost } from '../lib/api'
import { Button } from '../ui/Button'
import { load } from './store'
import { criterionFor } from './rubric'
import { proposedSummaryOf } from './step-card'
import { COMPOSED, DRAWN, meaningOf } from './proposal-copy'
import { UNTITLED, acceptInto, bandsLine, partsOf, planFor, simulate, type Proposal } from './proposal'
import { describedAs, forgetRequest, keepRequest, readRequest, type ProposalRequest } from './proposal-request'

/** Past the server's own 60 s (`ai.authoring.timeout_ms`), so its answer always arrives first. */
export const DRAFT_TIMEOUT_MS = 90_000

/** When each drafting stage begins, from the moment the draft is asked for — the drawing's four. */
const STAGE_STARTS_MS = [0, 1_500, 4_000, 14_000] as const

/** The draft the editor opens once accepting has made it. */
export type Accepted = { id: string; status: string; archetype: string }

type Draft = { id: string; status: string; archetype: string; schema: Funnel }

type Phase =
  | { kind: 'drafting'; since: number }
  | { kind: 'proposal'; proposal: Proposal }
  | { kind: 'failed'; message: string; retry: boolean }

export function ProposalView({
  workspaceId,
  onAccepted,
}: {
  workspaceId: string
  /** The funnel exists and holds the accepted parts; the editor takes it from here. */
  onAccepted: (draft: Accepted) => void
}) {
  const [request, setRequest] = useState<ProposalRequest | null>(() => readRequest())
  const [phase, setPhase] = useState<Phase>({ kind: 'drafting', since: Date.now() })
  /** Which draft is current — an answer to an older one, overtaken by "Draft it again as", is dropped. */
  const asked = useRef(0)
  /**
   * Whether arriving has asked already. React's development StrictMode runs an effect twice, and
   * each run here is a model call counted against the day's limit — found when the E2E run saw two
   * drafts for one arrival (2026-09-30).
   */
  const arrived = useRef(false)

  const draft = async (next: ProposalRequest) => {
    const mine = ++asked.current
    setPhase({ kind: 'drafting', since: Date.now() })
    try {
      const { proposal } = await apiPost<{ proposal: Proposal }>(
        `/v1/w/${workspaceId}/proposals`,
        {
          description: describedAs(next.business, next.customer),
          ...(next.archetype !== undefined ? { archetype: next.archetype } : {}),
        },
        { signal: AbortSignal.timeout(DRAFT_TIMEOUT_MS) },
      )
      if (mine === asked.current) setPhase({ kind: 'proposal', proposal })
    } catch (error) {
      if (mine !== asked.current) return
      // A 4xx names its own fix — a limit, the kill switch, a stop, a permission (ARCH §4.3). A 5xx
      // carries no message (`toErrorBody`), and neither does a network failure or a timeout.
      const own = error instanceof ApiFailure && error.status >= 400 && error.status < 500
      setPhase({
        kind: 'failed',
        message: own ? (error as ApiFailure).message : COMPOSED.failedTransient,
        retry: !own,
      })
    }
  }

  useEffect(() => {
    // Nothing asked for in this tab — opened directly, or storage refused the hand-over. Funnels
    // is where a description is asked for, so that is where this goes.
    if (request === null) {
      window.location.replace('/funnels')
      return
    }
    // Asked once, on arrival; every later draft is a button's own.
    if (arrived.current) return
    arrived.current = true
    void draft(request)
  }, [])

  if (request === null) return null

  const redraft = (archetype: Archetype) => {
    const next = { ...request, archetype }
    keepRequest(next)
    setRequest(next)
    void draft(next)
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-mdx-14 px-mdx-24 pb-mdx-40 pt-mdx-24">
      {phase.kind === 'drafting' ? (
        <DraftingCard request={request} since={phase.since} />
      ) : phase.kind === 'failed' ? (
        <FailedCard
          request={request}
          message={phase.message}
          onRetry={phase.retry ? () => void draft(request) : null}
        />
      ) : (
        <Review
          workspaceId={workspaceId}
          proposal={phase.proposal}
          onRedraft={redraft}
          onAccepted={onAccepted}
        />
      )}
    </div>
  )
}

/* ── drafting ───────────────────────────────────────────────────────────── */

/** The card `drafting` and a failed draft share: 640px drawn, the reading measure built. */
const CARD_FRAME =
  'mx-auto mt-mdx-24 flex w-full max-w-mdx-measure flex-col gap-mdx-18 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-28 shadow-mdx-card'

const EYEBROW = 'text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate'

function Quotes({ request }: { request: ProposalRequest }) {
  const words = [request.business, request.customer].map((text) => text.trim()).filter((text) => text !== '')
  return (
    <div className="flex flex-col gap-mdx-8">
      {words.map((text, index) => (
        <p key={index} className="m-0 text-pretty text-mdx-15 text-mdx-slate">
          {DRAWN.quoted(text)}
        </p>
      ))}
    </div>
  )
}

/** How long it has been, re-read twice a second while the card is up. */
function useElapsed(since: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(timer)
  }, [])
  return Math.max(0, now - since)
}

function DraftingCard({ request, since }: { request: ProposalRequest; since: number }) {
  const elapsed = useElapsed(since)
  const current = STAGE_STARTS_MS.reduce<number>((stage, start, index) => (elapsed >= start ? index : stage), 0)
  return (
    <section aria-label={DRAWN.draftingEyebrow} className={CARD_FRAME}>
      <span className={EYEBROW}>{DRAWN.draftingEyebrow}</span>
      <Quotes request={request} />
      <ol aria-live="polite" className="m-0 flex list-none flex-col gap-mdx-12 border-t border-mdx-line p-0 pt-mdx-18">
        {DRAWN.stages.map((stage, index) => (
          <Stage key={stage} label={stage} state={index < current ? 'done' : index === current ? 'active' : 'waiting'} />
        ))}
      </ol>
      <span className="text-mdx-13 text-mdx-slate">{DRAWN.draftingNote}</span>
    </section>
  )
}

function Stage({ label, state }: { label: string; state: 'done' | 'active' | 'waiting' }) {
  return (
    <li
      className={[
        'flex items-center gap-mdx-12 text-mdx-15',
        state === 'active' ? 'font-mdx-semibold' : '',
        state === 'waiting' ? 'text-mdx-slate' : '',
      ].join(' ')}
    >
      {state === 'done' ? (
        <span
          aria-hidden="true"
          className="flex size-mdx-22 flex-none items-center justify-center rounded-mdx-pill bg-mdx-brand text-mdx-12 font-mdx-bold text-mdx-white"
        >
          ✓
        </span>
      ) : state === 'active' ? (
        <span
          aria-hidden="true"
          className="flex size-mdx-22 flex-none items-center justify-center rounded-mdx-pill border-2 border-solid border-mdx-brand"
        >
          <span className="size-mdx-8 animate-mdx-pulse rounded-mdx-pill bg-mdx-brand" />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="size-mdx-22 flex-none rounded-mdx-pill border-solid border-mdx-line-strong"
          // The drawn 1.5px edge — `--mdx-space-1-5`, which Tailwind v4 feeds to width and padding
          // but not to border-width (EditorChrome's saving ring, the same reason).
          style={{ borderWidth: 'var(--mdx-space-1-5)', boxSizing: 'border-box' }}
        />
      )}
      {label}
      <span className="sr-only">
        {state === 'done' ? DRAWN.stageDone : state === 'active' ? DRAWN.stageActive : DRAWN.stageWaiting}
      </span>
    </li>
  )
}

/* ── a draft that could not be made ────────────────────────────────────── */

function FailedCard({
  request,
  message,
  onRetry,
}: {
  request: ProposalRequest
  message: string
  onRetry: (() => void) | null
}) {
  return (
    <section aria-label={COMPOSED.failedEyebrow} className={CARD_FRAME}>
      <span className={EYEBROW}>{COMPOSED.failedEyebrow}</span>
      <Quotes request={request} />
      <p role="alert" className="m-0 border-t border-mdx-line pt-mdx-18 text-pretty text-mdx-15 text-mdx-ink">
        {message}
      </p>
      <span className="flex flex-wrap gap-mdx-12">
        <Button onClick={() => window.location.assign('/library')}>{DRAWN.fromTemplate}</Button>
        {onRetry !== null ? (
          <Button variant="secondary" onClick={onRetry}>
            {DRAWN.tryAgain}
          </Button>
        ) : null}
      </span>
    </section>
  )
}

/* ── the review ─────────────────────────────────────────────────────────── */

function Review({
  workspaceId,
  proposal,
  onRedraft,
  onAccepted,
}: {
  workspaceId: string
  proposal: Proposal
  onRedraft: (archetype: Archetype) => void
  onAccepted: (draft: Accepted) => void
}) {
  const { funnel } = proposal
  const parts = useMemo(() => partsOf(proposal), [proposal])
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set(funnel.steps.map((step) => step.id)))
  const [accepting, setAccepting] = useState<'all' | 'some' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const all = useMemo(() => new Set(funnel.steps.map((step) => step.id)), [funnel])
  const some = useMemo(() => planFor(proposal, ticked), [proposal, ticked])
  const someSaves = useMemo(() => simulate(proposal, some).ok, [proposal, some])

  const toggle = (stepId: string) =>
    setTicked((previous) => {
      const next = new Set(previous)
      if (next.has(stepId)) next.delete(stepId)
      else next.add(stepId)
      return next
    })

  const accept = async (which: 'all' | 'some') => {
    const plan = which === 'all' ? planFor(proposal, all) : some
    if (!simulate(proposal, plan).ok) return
    setProblem(null)
    setAccepting(which)
    try {
      // The archetype is fixed here and never after (DECISION-119, DECISION-167); the save takes it
      // from the row, so a proposal altered in the browser cannot change it later either.
      const created = await apiPost<{ id: string }>(`/v1/w/${workspaceId}/funnels`, {
        name: UNTITLED,
        archetype: proposal.archetype,
      })
      const draft = await apiGet<Draft>(`/v1/w/${workspaceId}/funnels/${created.id}`)
      load(draft.id, draft.schema)
      acceptInto(plan)
      forgetRequest()
      onAccepted({ id: draft.id, status: draft.status, archetype: draft.archetype })
    } catch (error) {
      setProblem(error instanceof ApiFailure ? error.message : COMPOSED.acceptFailed)
      setAccepting(null)
    }
  }

  const discard = () => {
    forgetRequest()
    window.location.assign('/funnels')
  }

  const kinds = ARCHETYPES.filter((kind) => kind !== proposal.archetype)
  const bands = bandsLine(funnel)
  const busy = accepting !== null

  return (
    <div className="mx-auto flex w-full max-w-mdx-measure flex-col gap-mdx-14">
      <section className="flex flex-col gap-mdx-12 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
        <span className={EYEBROW}>{DRAWN.proposedEyebrow}</span>
        <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-8">
          <h2 className="m-0 text-mdx-17 font-mdx-semibold tracking-mdx-h3">{UNTITLED}</h2>
          <span className="inline-flex items-center rounded-mdx-pill bg-mdx-ground px-mdx-10 py-mdx-4 text-mdx-12 font-mdx-semibold text-mdx-slate">
            {DRAWN.kind[proposal.archetype]}
          </span>
        </div>
        <span className="max-w-mdx-measure-56 text-pretty text-mdx-15">{meaningOf(proposal.archetype)}</span>
        <div className="flex flex-wrap items-center gap-mdx-8 border-t border-mdx-line pt-mdx-12">
          <span className="mr-mdx-4 text-mdx-13 text-mdx-slate">{DRAWN.draftAgainAs}</span>
          {kinds.map((kind) => (
            <button
              key={kind}
              type="button"
              disabled={busy}
              onClick={() => onRedraft(kind)}
              className="cursor-pointer whitespace-nowrap rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-6 text-mdx-13 font-mdx-semibold text-mdx-ink transition-colors ease-mdx-swift hover:bg-mdx-ground disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
            >
              {DRAWN.kind[kind]}
            </button>
          ))}
        </div>
      </section>

      <div className="mt-mdx-6 flex flex-wrap items-baseline gap-x-mdx-14 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{DRAWN.steps}</span>
        <span className="text-mdx-13 tabular-nums text-mdx-slate">{DRAWN.ticked(ticked.size, funnel.steps.length)}</span>
      </div>

      <ol className="m-0 flex list-none flex-col gap-mdx-10 p-0">
        {parts.steps.map((step) => {
          const on = ticked.has(step.id)
          const blocks = (funnel.steps.find((candidate) => candidate.id === step.id)?.blocks ?? [])
            .map((id) => funnel.blocks.find((block) => block.id === id))
            .filter((block): block is Block => block !== undefined)
          const scored = blocks.some((block) => criterionFor(funnel, block.id) !== undefined)
          const summary = proposedSummaryOf(funnel, blocks)
          return (
            <li
              key={step.id}
              className={[
                'flex items-start gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-18 py-mdx-16 shadow-mdx-card transition-opacity ease-mdx-swift',
                on ? '' : 'opacity-mdx-recessed',
              ].join(' ')}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={busy}
                aria-label={DRAWN.include(step.number, step.title)}
                onChange={() => toggle(step.id)}
                className="m-0 mt-mdx-4 size-mdx-18 flex-none cursor-pointer accent-mdx-brand"
              />
              <span className="flex min-w-0 flex-1 flex-col gap-mdx-10">
                <span className="flex flex-wrap items-baseline gap-x-mdx-12 gap-y-mdx-6">
                  <span className={`${EYEBROW} tabular-nums`}>{DRAWN.step(step.number)}</span>
                  <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">{step.title}</span>
                  {scored ? (
                    <span className="flex-none rounded-mdx-pill bg-mdx-brand-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-semibold text-mdx-brand-deep">
                      {DRAWN.scored}
                    </span>
                  ) : null}
                  <span className="ml-auto flex-none rounded-mdx-pill bg-mdx-brand-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-brand-deep">
                    {DRAWN.proposed}
                  </span>
                </span>
                {summary !== null ? (
                  <span className="block rounded-mdx-control bg-mdx-ground px-mdx-12 py-mdx-10 text-mdx-13 text-mdx-slate">
                    {summary}
                  </span>
                ) : null}
              </span>
            </li>
          )
        })}
      </ol>

      {bands !== null ? <span className="text-mdx-13 text-mdx-slate">{bands}</span> : null}
      {parts.customer !== null ? <span className="text-mdx-13 text-mdx-slate">{COMPOSED.idealCustomer}</span> : null}

      {!someSaves ? (
        <p role="status" className="m-0 text-mdx-13 text-mdx-error">
          {COMPOSED.partRefused}
        </p>
      ) : null}
      {problem !== null ? (
        <p role="alert" className="m-0 text-mdx-15 text-mdx-error">
          {problem}
        </p>
      ) : null}

      <div className="mt-mdx-4 flex flex-col gap-mdx-12 border-t border-mdx-line pt-mdx-16 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-mdx-20">
        <span className="flex min-w-0 sm:flex-1 sm:basis-mdx-row-control">
          <span className="max-w-mdx-measure-48 text-pretty text-mdx-13 text-mdx-slate">{DRAWN.acceptNote}</span>
        </span>
        {/* The DOM order is the phone's — the primary first — and a row reverses it at wider widths
            into the drawn "Discard · Accept 5 of 6 steps · Accept all". */}
        <span className="flex flex-col gap-mdx-12 sm:flex-row-reverse sm:flex-wrap">
          <Button
            className="w-full sm:w-auto"
            disabled={busy}
            loading={accepting === 'all' ? DRAWN.acceptAll : undefined}
            onClick={() => void accept('all')}
          >
            {DRAWN.acceptAll}
          </Button>
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            disabled={busy || ticked.size === 0 || !someSaves}
            loading={accepting === 'some' ? DRAWN.acceptSome(ticked.size, funnel.steps.length) : undefined}
            onClick={() => void accept('some')}
          >
            {DRAWN.acceptSome(ticked.size, funnel.steps.length)}
          </Button>
          <Button variant="secondary" className="w-full sm:w-auto" disabled={busy} onClick={discard}>
            {DRAWN.discard}
          </Button>
        </span>
      </div>
    </div>
  )
}

